import { A } from "@solidjs/router";
import { getIconComponent } from "@storytell/bsky-icons";
import type { ExecutionMeta } from "@storytell/client";
import { isAutoGeneratedLabelName, isLightColor } from "@storytell/core";
import { type StreamingChatId, StreamingChatStatus } from "@storytell/streaming";
import type { AssetId, Label } from "@storytell/types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { TbLoader2 } from "solid-icons/tb";
import { type Component, createSignal, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useActionExecutions } from "~/components/actions/internal/useActionExecutions";
import { useI18n } from "~/i18n/context";
import { formatCredits } from "~/utils/creditFormat";
import { useWire } from "~/wire";

dayjs.extend(relativeTime);

interface ActionRunsListProps {
  actionId: string;
  /** chatId of the run currently displayed in the main content; used to highlight the active row. */
  activeChatId: string | undefined;
  /** Called after a row click; the parent uses this to keep the drawer open across navigation if desired. */
  onRunSelected?: (execution: ExecutionMeta) => void;
}

export const ActionRunsList: Component<ActionRunsListProps> = (props) => {
  const wire = useWire();
  const { t, currentLanguage } = useI18n();

  const { executions, executionsLoading } = useActionExecutions(() => props.actionId, 20);

  const handleClick = (execution: ExecutionMeta) => {
    wire.navigation.navigateToExecution(execution.chatId, execution.actionId, {
      projectId: execution.projectId,
    });
    props.onRunSelected?.(execution);
  };

  const MAX_VISIBLE_LABELS = 4;

  const labelsForExecution = (execution: ExecutionMeta): Label[] => {
    if (!execution.outcomeAssetId) return [];
    const labelIds = wire.rels.labelsForAsset(execution.outcomeAssetId as AssetId);
    const labels = labelIds.map((id) => wire.labels.get(id)).filter(Boolean) as Label[];
    return labels.toSorted((a, b) => {
      const aSystem = isAutoGeneratedLabelName(a.name);
      const bSystem = isAutoGeneratedLabelName(b.name);
      if (aSystem !== bSystem) return aSystem ? 1 : -1;
      return a.name.localeCompare(b.name);
    });
  };

  const isExecutionRunning = (execution: ExecutionMeta) => {
    const executionChatId = execution.chatId as StreamingChatId;
    if (wire.stream.chat.getStatus(executionChatId) === StreamingChatStatus.GENERATING) {
      return true;
    }
    return wire.chat.getChatId() === executionChatId && wire.chat.isChatStreaming() && !execution.outcomeError;
  };

  return (
    <Show
      when={executionsLoading()}
      fallback={
        <Show
          when={executions() && executions().length > 0}
          fallback={<div class="py-4 text-center text-sm text-muted-text">{t("actions.run_history_empty")}</div>}
        >
          <ul class="space-y-2">
            <For each={executions()}>
              {(execution) => {
                const displayTitle = () => execution.title || t("actions.run_history_empty_title");
                const relTime = () => dayjs(execution.modifiedAt || execution.createdAt).fromNow();
                const isActive = () => execution.chatId === props.activeChatId;
                const labels = () => labelsForExecution(execution);
                const [labelsExpanded, setLabelsExpanded] = createSignal(false);
                const visibleLabels = () => (labelsExpanded() ? labels() : labels().slice(0, MAX_VISIBLE_LABELS));
                const hiddenLabelCount = () => Math.max(0, labels().length - MAX_VISIBLE_LABELS);
                const href = () =>
                  wire.navigation.buildUrl({
                    projectId: execution.projectId,
                    primaryId: execution.chatId,
                    actionCtx: execution.actionId,
                  });
                return (
                  <li>
                    <A
                      href={href()}
                      onClick={(e) => {
                        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        e.preventDefault();
                        handleClick(execution);
                      }}
                      class="group block w-full cursor-pointer rounded-lg border bg-panel p-2.5 text-left transition-colors"
                      classList={{
                        "border-brand/60 bg-brand/5": isActive(),
                        "border-primary-border hover:border-brand/30": !isActive(),
                      }}
                    >
                      <div class="flex min-w-0 items-center justify-between gap-2">
                        <h4
                          class="min-w-0 flex-1 truncate text-sm font-medium text-primary-text transition-colors"
                          classList={{
                            "text-brand": isActive(),
                            "group-hover:text-brand": !isActive(),
                          }}
                        >
                          {displayTitle()}
                        </h4>
                        <Show when={isExecutionRunning(execution)}>
                          <TbLoader2 class="size-3.5 shrink-0 animate-spin text-muted-text" />
                        </Show>
                      </div>
                      <p class="mt-0.5 text-xs text-muted-text">
                        {relTime()}
                        <Show when={execution.credits > 0}>
                          {" · "}
                          {t("actions.run_credits", { n: formatCredits(execution.credits, currentLanguage()) })}
                        </Show>
                      </p>
                      <Show when={labels().length > 0}>
                        <div class="mt-1.5 flex min-w-0 flex-wrap items-center gap-1">
                          <For each={visibleLabels()}>
                            {(label) => (
                              <span class="inline-flex items-center gap-1 rounded-full border border-primary-border bg-tile px-1.5 py-0.5 text-[10px] leading-none text-secondary-text">
                                <span
                                  class="grid size-3 shrink-0 place-content-center rounded-full bg-(--label-color)"
                                  style={{
                                    "--label-color": label.color,
                                    "--label-icon": isLightColor(label.color) ? "black" : "white",
                                  }}
                                >
                                  <Dynamic
                                    component={getIconComponent(label.icon)}
                                    class="size-2.5 text-(--label-icon)"
                                  />
                                </span>
                                <span>{label.name}</span>
                              </span>
                            )}
                          </For>
                          <Show when={hiddenLabelCount() > 0}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setLabelsExpanded((v) => !v);
                              }}
                              class="inline-flex shrink-0 cursor-pointer items-center rounded-full border border-primary-border bg-tile px-1.5 py-0.5 text-[10px] leading-none text-secondary-text transition-colors hover:bg-tile-hover hover:text-primary-text"
                            >
                              {labelsExpanded() ? t("common.show_less") : t("labels_n_more", { n: hiddenLabelCount() })}
                            </button>
                          </Show>
                        </div>
                      </Show>
                    </A>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      }
    >
      <div class="flex items-center justify-center py-4">
        <TbLoader2 class="size-4 animate-spin text-muted-text" />
      </div>
    </Show>
  );
};
